import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, UserRole } from '../types';
import { api } from '../services/apiService';

interface UserManagementModalProps {
  companyId: string;
  currentUser: User;
  onClose: () => void;
  onUpdate: () => void;
}

const UserManagementModal: React.FC<UserManagementModalProps> = ({ companyId, currentUser, onClose, onUpdate }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  
  // États du formulaire
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: UserRole.USER,
    inactivityTimeout: '' as string
  });

  const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    loadUsers();
  }, [companyId]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const usersData = await api.getCompanyUsers(companyId);
      const list = usersData || [];
      if (
        currentUser.role === UserRole.COMPANY_ADMIN &&
        String(currentUser.companyId) === String(companyId) &&
        !list.some(u => String(u.id) === String(currentUser.id))
      ) {
        setUsers([currentUser, ...list]);
      } else {
        setUsers(list);
      }
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    }
    setIsLoading(false);
  };

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: UserRole.USER,
      inactivityTimeout: ''
    });
    setEditingUser(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      inactivityTimeout: user.inactivityTimeout !== undefined ? String(user.inactivityTimeout) : ''
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      showToast('Le nom est obligatoire', 'error');
      return;
    }
    if (!formData.email.trim()) {
      showToast("L'email est obligatoire", 'error');
      return;
    }
    if (!editingUser && !formData.password) {
      showToast('Le mot de passe est obligatoire pour un nouvel utilisateur', 'error');
      return;
    }

    try {
      if (editingUser) {
        // Modification
        const timeoutVal = formData.inactivityTimeout === '' ? undefined : parseInt(formData.inactivityTimeout, 10);
        await api.updateUser(editingUser.id, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          inactivityTimeout: timeoutVal,
          ...(formData.password && { password: formData.password })
        });
        showToast('Utilisateur modifié avec succès', 'success');
      } else {
        // Création
        await api.createUser({
          companyId,
          name: formData.name,
          email: formData.email,
          role: formData.role,
          password: formData.password
        });
        showToast('Utilisateur créé avec succès', 'success');
      }

      setShowAddModal(false);
      resetForm();
      await loadUsers();
      onUpdate();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showToast('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (userId: string) => {
    if (userId === currentUser.id) {
      showToast('Vous ne pouvez pas vous supprimer vous-même', 'error');
      return;
    }

    try {
      await api.deleteUser(userId);
      showToast('Utilisateur supprimé avec succès', 'success');
      setDeleteConfirmId(null);
      await loadUsers();
      onUpdate();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.COMPANY_ADMIN:
        return 'Administrateur';
      case UserRole.MANAGER:
        return 'Manager';
      case UserRole.USER:
        return 'Utilisateur';
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.COMPANY_ADMIN:
        return 'bg-purple-500 text-white';
      case UserRole.USER:
        return 'bg-blue-500 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 orion-user-modal-backdrop">
      <div className="absolute inset-0" onClick={onClose}></div>

      <div className="relative orion-user-modal-container w-full max-w-4xl max-h-[90vh] flex flex-col rounded-[3rem] overflow-hidden">
        
        {/* Toast */}
        {toast && (
          <div className={`absolute top-6 right-6 z-[200] px-6 py-3 rounded-2xl shadow-2xl ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white text-sm font-bold animate-in slide-in-from-top-2`}>
            {toast.msg}
          </div>
        )}

        {/* Header */}
        <div className="flex-none px-8 py-6 orion-user-modal-header flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black text-white">Gestion des utilisateurs</h2>
            <p className="text-sm text-slate-400 mt-1">{users.length} utilisateur{users.length > 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openAddModal}
              className="px-6 py-3 orion-user-btn-primary text-white rounded-2xl text-xs font-black uppercase tracking-widest"
            >
              + Ajouter
            </button>
            <button onClick={onClose} className="p-2 orion-user-btn-icon rounded-xl text-slate-400 hover:text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Liste des utilisateurs */}
        <div className="flex-1 overflow-y-auto p-8">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-slate-400 mt-4">Chargement...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-slate-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="text-slate-400 font-medium">Aucun utilisateur</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="orion-user-card rounded-2xl p-5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-black text-lg">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-bold text-white">{user.name}</h3>
                          {user.id === currentUser.id && (
                            <span className="text-xs bg-amber-500/20 text-amber-300 border border-amber-500/40 px-2 py-1 rounded-full font-bold">Vous</span>
                          )}
                          <span className={`text-xs px-3 py-1 rounded-full font-bold text-white ${
                            user.role === UserRole.COMPANY_ADMIN ? 'orion-role-badge-admin' :
                            user.role === UserRole.MANAGER ? 'orion-role-badge-manager' :
                            'orion-role-badge-user'
                          }`}>
                            {getRoleLabel(user.role)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{user.email}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-2 orion-user-btn-icon rounded-xl text-slate-400 hover:text-white"
                        title="Modifier"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>

                      {user.id !== currentUser.id && (
                        <>
                          {deleteConfirmId === user.id ? (
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleDelete(user.id)}
                                className="px-3 py-1 bg-red-500/90 text-white text-xs font-bold rounded-lg hover:bg-red-500 border border-red-400/40"
                              >
                                Confirmer
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="px-3 py-1 orion-user-card text-slate-300 text-xs font-bold rounded-lg hover:text-white"
                              >
                                Annuler
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(user.id)}
                              className="p-2 orion-user-btn-icon orion-user-btn-delete rounded-xl text-red-400 hover:text-red-300"
                              title="Supprimer"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal Ajout/Edition */}
      {showAddModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 orion-user-modal-backdrop">
          <div className="orion-user-modal-container w-full max-w-md rounded-[2rem] p-8">
            <h3 className="text-xl font-black mb-6 text-white">
              {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Nom complet *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Jean Dupont"
                  className="w-full orion-user-input rounded-xl px-4 py-3 text-sm font-bold"
                  required
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="jean@entreprise.com"
                  className="w-full orion-user-input rounded-xl px-4 py-3 text-sm font-bold"
                  required
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                  Mot de passe {editingUser ? '(laisser vide pour ne pas changer)' : '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={editingUser ? 'Nouveau mot de passe' : 'Mot de passe'}
                  className="w-full orion-user-input rounded-xl px-4 py-3 text-sm font-bold"
                  required={!editingUser}
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Rôle *</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: UserRole.USER })}
                    className={`p-4 rounded-xl text-left ${formData.role === UserRole.USER ? 'orion-role-option-selected' : 'orion-role-option'}`}
                  >
                    <div className="text-xs font-black text-white uppercase mb-1">Utilisateur</div>
                    <div className="text-[10px] text-slate-400 leading-tight">Ses leads uniquement</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: UserRole.MANAGER })}
                    className={`p-4 rounded-xl text-left ${formData.role === UserRole.MANAGER ? 'orion-role-option-selected' : 'orion-role-option'}`}
                  >
                    <div className="text-xs font-black text-white uppercase mb-1">Manager</div>
                    <div className="text-[10px] text-slate-400 leading-tight">Son équipe + ses leads</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: UserRole.COMPANY_ADMIN })}
                    className={`p-4 rounded-xl text-left ${formData.role === UserRole.COMPANY_ADMIN ? 'orion-role-option-selected' : 'orion-role-option'}`}
                  >
                    <div className="text-xs font-black text-white uppercase mb-1">Admin</div>
                    <div className="text-[10px] text-slate-400 leading-tight">Accès complet</div>
                  </button>
                </div>
              </div>

              {/* Timeout d'inactivité (édition uniquement) */}
              {editingUser && (
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                    Déconnexion auto (minutes)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={480}
                    title="Délai d'inactivité en minutes (laisser vide = utilise le défaut de la compagnie)"
                    value={formData.inactivityTimeout}
                    onChange={e => setFormData({ ...formData, inactivityTimeout: e.target.value })}
                    placeholder="Délai compagnie (défaut)"
                    className="w-full orion-user-input rounded-xl px-4 py-3 text-sm font-bold"
                  />
                  <p className="text-[10px] text-slate-400 mt-1 ml-1">Laisser vide pour utiliser le délai par défaut de la compagnie.</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="flex-1 py-3 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 orion-user-btn-primary text-white rounded-xl text-xs font-bold uppercase tracking-widest"
                >
                  {editingUser ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default UserManagementModal;
